/**
 * Payment links and the embeddable buy button: everything that decides what a
 * `/buy/<productId>` URL does, and everything that builds the URL and the HTML a
 * seller pastes into their own site.
 *
 * WHY THIS IS ONE PURE MODULE. Three surfaces have to agree about the same few
 * facts: the seller dialog that BUILDS a link (which tier, which tag, what the
 * button looks like), the `/buy` page that READS it (which products may go
 * straight to checkout and which must fall back to the product page), and
 * `public/embed.js`, which upgrades a pasted button. The first two import this
 * file; the third is plain served JavaScript that cannot, so the patterns it
 * repeats are pinned by src/lib/embed-script.test.ts instead.
 *
 * WHAT A LINK MAY DO, AND WHY IT IS CONSERVATIVE.
 *
 *   - Only a `live` product goes to checkout. The server refuses everything
 *     else with "Product <id> is no longer available.", naming the raw id, and
 *     checkout's "Try again" can never succeed. Unlisted products ARE live and
 *     are the main use case (a seller selling only through their own funnel),
 *     so `listedOnMarketplace` is deliberately not read here.
 *   - Pay-what-you-want needs a buyer-named amount and a tiered product needs a
 *     tier. Checkout has no field for either, so a link that cannot supply one
 *     falls back to the product page, which already has both pickers.
 *   - The link never shows a price. A price shown to an EU or GB visitor needs
 *     VatNotice, which needs the server-resolved country; neither exists on a
 *     third-party page, and `/buy` itself forwards before anything is read.
 *
 * WHAT IT DOES TO THE CART. Checkout charges the WHOLE cart and has no way to
 * remove a line, and the receipt page then clears the whole cart. So a link that
 * quietly appended its product to a returning buyer's cart would bill them for
 * everything else in it too. `planLinkCart` goes straight to checkout only when
 * nothing else is there; otherwise the buyer is asked.
 */

import { BASE_URL } from './site.ts';
import { cartLineProductId, MAX_CART_ITEMS } from './cart-line-id.ts';
import { currentLinePriceAud } from './cart-pricing.ts';
import { isSubscriptionTier, tierFrequency } from './product-form-shared.ts';

/** Path segment the payment-link route lives under. */
export const PAYMENT_LINK_PATH = '/buy';

/**
 * A product document id as it can appear in a link.
 *
 * Firestore auto-ids are alphanumeric, imported ids are `imp_<...>` with every
 * part stripped to alphanumerics, and the one historical scheme with a hyphen is
 * `prod-XXX` (see the invariant at the top of src/lib/cart-line-id.ts). Anything
 * else, and in particular anything with a `/` from a decoded `%2F`, is refused
 * before it can reach `doc()`, which throws on a slash. 200 matches the server's
 * zod bound on `productId`.
 */
const PRODUCT_ID_PATTERN = /^(?:prod-)?[A-Za-z0-9_]{1,195}$/;

/**
 * A tier id. Written as `Date.now()` numbers by the product forms and as strings
 * by the import path; the server compares them with `String()`, so any short
 * token of these characters round-trips.
 */
const TIER_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

/**
 * The seller's own tracking tag (`?ref=youtube`). Short and boring on purpose:
 * it is stored on the order and shown back to the seller, so it must never be a
 * vehicle for markup or a second URL.
 */
export const REF_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
export const REF_MAX_LENGTH = 40;

/** How the buyer arrived: a bare link, or the embeddable button (`?via=button`). */
export type LinkSource = 'link' | 'button';

export function isValidProductId(id: unknown): id is string {
  return typeof id === 'string' && PRODUCT_ID_PATTERN.test(id);
}

export function sanitizeTierId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return TIER_ID_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function sanitizeRef(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return REF_PATTERN.test(trimmed) ? trimmed : undefined;
}

export type PaymentLinkOptions = {
  productId: string;
  tierId?: string;
  ref?: string;
  source?: LinkSource;
};

/**
 * `/buy/<id>?tier=..&ref=..&via=button`, with every invalid part dropped rather
 * than escaped: a tag the `/buy` page would refuse must not be in the URL the
 * seller copies, or the seller's reports would silently lose it.
 */
export function buildPaymentLinkPath({ productId, tierId, ref, source }: PaymentLinkOptions): string {
  const params = new URLSearchParams();
  const tier = sanitizeTierId(tierId);
  const tag = sanitizeRef(ref);
  if (tier) params.set('tier', tier);
  if (tag) params.set('ref', tag);
  if (source === 'button') params.set('via', 'button');
  const query = params.toString();
  return `${PAYMENT_LINK_PATH}/${encodeURIComponent(productId)}${query ? `?${query}` : ''}`;
}

export function buildPaymentLinkUrl(origin: string, options: PaymentLinkOptions): string {
  return `${origin.replace(/\/+$/, '')}${buildPaymentLinkPath(options)}`;
}

/**
 * The origin a link is built against.
 *
 * Production links always name the canonical host, never `window.location`,
 * which on App Hosting can be a `*.hosted.app` origin that must not end up
 * pasted into a seller's website. In development the current origin is used so
 * a link copied from localhost:9010 can actually be tested there.
 */
export function paymentLinkOrigin(currentOrigin: string | undefined, nodeEnv: string | undefined): string {
  if (nodeEnv === 'development' && currentOrigin && /^https?:\/\//.test(currentOrigin)) {
    return currentOrigin.replace(/\/+$/, '');
  }
  return BASE_URL;
}

export type PaymentLinkQuery = {
  tierId?: string;
  ref?: string;
  source: LinkSource;
};

/** Read `?tier`, `?ref` and `?via` off a `/buy` URL, dropping anything malformed. */
export function parsePaymentLinkQuery(get: (key: string) => string | null | undefined): PaymentLinkQuery {
  const tierId = sanitizeTierId(get('tier'));
  const ref = sanitizeRef(get('ref'));
  return {
    ...(tierId ? { tierId } : {}),
    ...(ref ? { ref } : {}),
    source: get('via') === 'button' ? 'button' : 'link',
  };
}

// ---------------------------------------------------------------------------
// Deciding what a link does
// ---------------------------------------------------------------------------

export type LinkTier = {
  id?: unknown;
  name?: unknown;
  price?: unknown;
  purchaseType?: string;
  subscriptionFrequency?: string;
};

/**
 * The slice of a product document the link needs. Structural, so both the
 * client `Product` and a raw Firestore map satisfy it.
 */
export type LinkProduct = {
  id: string;
  name?: string | null;
  status?: string | null;
  sellerId?: string | null;
  pricingModel?: string | null;
  price?: number | null;
  discountedPrice?: number | null;
  subscriptionPrice?: number | null;
  subscriptionFrequency?: string | null;
  priceTiers?: LinkTier[] | null;
  image?: string | { src?: string | null } | null;
};

/**
 * A cart line, structurally identical to `CartLineItem` in
 * src/context/cart-context.tsx (which cannot be imported by node tests).
 * Optional fields are OMITTED rather than set to undefined: the signed-in cart
 * is mirrored straight into Firestore, and the SDK rejects a write that
 * contains an undefined value.
 */
export type LinkCartLine = {
  id: string;
  name: string;
  price: number;
  image: string;
  isSubscription?: boolean;
  subscriptionFrequency?: string;
  tierId?: string;
};

export type PaymentLinkOutcome =
  /** The id in the URL is not a product id at all. */
  | { kind: 'invalid' }
  /** No readable product (missing, or someone else's draft) or not on sale. */
  | { kind: 'unavailable'; reason: 'missing' | 'not-on-sale' }
  /** The seller opened their own link; the server would refuse the purchase. */
  | { kind: 'own-product' }
  /** Needs a choice checkout cannot offer; hand over to the product page. */
  | { kind: 'product-page'; reason: 'pwyw' | 'choose-tier' }
  /** The buyer already owns this one-off product; the server would refuse it. */
  | { kind: 'owned' }
  /**
   * A subscription or a free product, and nobody is signed in. Both need an
   * account at checkout, so the buyer signs in or up FIRST and comes back to
   * this link, which then plans against their account's cart.
   */
  | { kind: 'needs-account'; reason: 'subscription' | 'free' }
  /** Go: this is the line to put in the cart. */
  | { kind: 'checkout'; line: LinkCartLine };

export function linkTierFor(product: LinkProduct, tierId: string | undefined): LinkTier | undefined {
  if (!tierId || product.pricingModel !== 'tiered') return undefined;
  const tiers = Array.isArray(product.priceTiers) ? product.priceTiers : [];
  // String-compared, as the server does: ids are numbers on some documents and
  // strings on others.
  return tiers.find((t) => t && String(t.id) === String(tierId));
}

function coverUrl(image: LinkProduct['image']): string {
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object' && typeof image.src === 'string') return image.src;
  return '';
}

/**
 * The cart line for a product (and tier), shaped exactly as the product page's
 * handleAddToCart shapes it, so the cart, checkout and the server's
 * one-subscription rule treat a linked line and a hand-added one identically.
 *
 * The price is `currentLinePriceAud`, the client mirror of what the server
 * charges. It is display only: the server re-prices every line from the
 * product document and never trusts this number.
 */
export function buildLinkCartLine(product: LinkProduct, tier?: LinkTier): LinkCartLine {
  const name = (typeof product.name === 'string' && product.name.trim()) || 'Product';
  const image = coverUrl(product.image);

  if (product.pricingModel === 'tiered' && tier) {
    const tierId = String(tier.id);
    const tierName = typeof tier.name === 'string' ? tier.name.trim() : '';
    return {
      id: `${product.id}-${tierId}`,
      name: tierName ? `${name} - ${tierName}` : name,
      price: currentLinePriceAud(product, tierId) ?? 0,
      image,
      tierId,
      ...(isSubscriptionTier(tier)
        ? { isSubscription: true, subscriptionFrequency: tierFrequency(tier) }
        : {}),
    };
  }

  if (product.pricingModel === 'subscription') {
    const frequency = typeof product.subscriptionFrequency === 'string' && product.subscriptionFrequency
      ? product.subscriptionFrequency
      : undefined;
    return {
      id: product.id,
      name,
      price: currentLinePriceAud(product) ?? (Number(product.price) || 0),
      image,
      isSubscription: true,
      ...(frequency ? { subscriptionFrequency: frequency } : {}),
    };
  }

  return { id: product.id, name, price: currentLinePriceAud(product) ?? 0, image };
}

export type PaymentLinkInput = {
  /** The raw route parameter. */
  productId: string;
  /** The product document, or null when it does not exist or cannot be read. */
  product: LinkProduct | null;
  tierId?: string;
  /** Signed-in viewer's uid, when there is one. */
  viewerUid?: string | null;
  /** Whether the viewer already holds a live entitlement to this product. */
  ownsProduct?: boolean;
  /**
   * False when nobody is signed in (or the session is anonymous). Left
   * undefined, the account check is skipped, which is how the own-product
   * screen asks "what would a buyer see" without it.
   */
  viewerSignedIn?: boolean;
};

/**
 * What a `/buy` URL should do for this viewer. Pure: the page waits for the
 * product, the viewer and their entitlements to load, then asks this once.
 *
 * The order matters. The seller check comes before the pay-what-you-want and
 * tier fallbacks so a seller testing their own link is told why it will not
 * take their money, rather than being bounced to a product page that also will
 * not. Ownership is checked only for one-off lines: the server's own checks for
 * subscriptions ("already subscribed", "another tier") are specific and
 * correct, and duplicating them here would only risk disagreeing with them.
 */
export function resolvePaymentLink(input: PaymentLinkInput): PaymentLinkOutcome {
  if (!isValidProductId(input.productId)) return { kind: 'invalid' };
  const product = input.product;
  if (!product) return { kind: 'unavailable', reason: 'missing' };
  if (product.status !== 'live') return { kind: 'unavailable', reason: 'not-on-sale' };

  if (input.viewerUid && product.sellerId && input.viewerUid === product.sellerId) {
    return { kind: 'own-product' };
  }

  if (product.pricingModel === 'pwyw') return { kind: 'product-page', reason: 'pwyw' };

  let tier: LinkTier | undefined;
  if (product.pricingModel === 'tiered') {
    tier = linkTierFor(product, input.tierId);
    if (!tier) return { kind: 'product-page', reason: 'choose-tier' };
  }

  const line = buildLinkCartLine(product, tier);
  if (input.ownsProduct && !line.isSubscription) return { kind: 'owned' };

  // WHY SIGN-IN COMES BEFORE THE CART, NOT AT CHECKOUT. Checkout already
  // gates subscriptions and free claims behind an account, so seeding a guest
  // cart and letting checkout ask would work, except for a returning account
  // holder on a new device: signing in merges their saved account cart into
  // the guest one, after the chooser that exists for exactly that case has
  // been skipped. A subscription then sits beside other items and the server
  // refuses the whole cart ("Subscriptions must be purchased on their own"),
  // and a free claim turns into a card form for whatever else was saved.
  // Signing in first, then coming back to this link, plans against the real
  // cart.
  if (input.viewerSignedIn === false) {
    if (line.isSubscription) return { kind: 'needs-account', reason: 'subscription' };
    if (product.pricingModel === 'free') return { kind: 'needs-account', reason: 'free' };
  }
  return { kind: 'checkout', line };
}

// ---------------------------------------------------------------------------
// Putting the line in the cart
// ---------------------------------------------------------------------------

export type LinkCartPlan =
  /** Nothing else is in the cart: set it to exactly this line and pay. */
  | { kind: 'direct'; items: LinkCartLine[] }
  /**
   * Other products are already in the cart. The buyer chooses between paying
   * for just this one (`justThis`, which REMOVES the others) and adding it to
   * the cart to review everything (`alongside`, null when that cannot work).
   */
  | {
      kind: 'choose';
      others: LinkCartLine[];
      justThis: LinkCartLine[];
      alongside: LinkCartLine[] | null;
      blocked: 'subscription' | 'full' | null;
    };

/**
 * How to seed the cart with a link's line without charging for anything the
 * buyer did not choose.
 *
 * Any existing line for the SAME product (the same line, a stale copy of it, or
 * another tier) is replaced by the link's line rather than counted as "other":
 * the server refuses a cart with one product twice, and the link names the tier
 * the buyer is buying now.
 *
 * `alongside` is null when the combined cart could never be paid for: a
 * subscription must be checked out on its own (server rule), and the cart holds
 * at most MAX_CART_ITEMS lines.
 */
export function planLinkCart(cart: readonly LinkCartLine[], line: LinkCartLine): LinkCartPlan {
  const productId = cartLineProductId(line.id, line.tierId);
  const others = cart.filter((item) => cartLineProductId(item.id, item.tierId) !== productId);
  if (others.length === 0) return { kind: 'direct', items: [line] };

  const blocked = line.isSubscription || others.some((item) => item.isSubscription)
    ? 'subscription'
    : others.length >= MAX_CART_ITEMS
      ? 'full'
      : null;
  return {
    kind: 'choose',
    others,
    justThis: [line],
    alongside: blocked ? null : [...others, line],
    blocked,
  };
}

/**
 * Rebuild cart lines from untrusted data (a Firestore document read directly,
 * outside the cart provider), keeping only well-formed lines. Mirrors the shape
 * check the cart provider applies to its localStorage copy.
 */
export function toLinkCartLines(raw: unknown): LinkCartLine[] {
  if (!Array.isArray(raw)) return [];
  const lines: LinkCartLine[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.id !== 'string' || item.id === '') continue;
    if (typeof item.name !== 'string') continue;
    if (typeof item.price !== 'number' || !Number.isFinite(item.price)) continue;
    if (typeof item.image !== 'string') continue;
    lines.push({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      ...(typeof item.isSubscription === 'boolean' ? { isSubscription: item.isSubscription } : {}),
      ...(typeof item.subscriptionFrequency === 'string' ? { subscriptionFrequency: item.subscriptionFrequency } : {}),
      ...(typeof item.tierId === 'string' ? { tierId: item.tierId } : {}),
    });
  }
  return lines;
}

/**
 * Every line either view of the cart holds, `primary` first, deduped by line id.
 *
 * WHY A UNION. A signed-in cart can reach the page from Firestore's persistent
 * local cache before the server has answered, so the provider's copy may be
 * missing lines added on another device. Planning against that copy alone
 * could pick `direct` and overwrite them. Planning against the union can only
 * err towards showing the chooser, which lists every line it is about to
 * remove or keep, so nothing leaves or returns to the cart without the buyer
 * seeing it named.
 */
export function mergeCartViews(primary: readonly LinkCartLine[], secondary: readonly LinkCartLine[]): LinkCartLine[] {
  const seen = new Set(primary.map((line) => line.id));
  return [...primary, ...secondary.filter((line) => !seen.has(line.id))];
}

// ---------------------------------------------------------------------------
// The embeddable button
// ---------------------------------------------------------------------------

export type ButtonTheme = 'brand' | 'dark' | 'light';

export const BUTTON_THEMES: Record<ButtonTheme, { label: string; background: string; color: string; border: string }> = {
  // The platform's default accent, DEFAULT_ACCENT_COLOR in
  // src/context/theme-context.tsx (hsl(241 90% 67%)), as hex because a pasted
  // snippet cannot read our CSS variables. It moves with that constant:
  // theme-preference.test.ts fails when the two differ, and public/embed.js
  // repeats it (embed-script.test.ts pins that copy to this one).
  brand: { label: 'supa.place purple', background: '#625ff7', color: '#ffffff', border: '#625ff7' },
  dark: { label: 'Dark', background: '#111113', color: '#ffffff', border: '#111113' },
  light: { label: 'Light', background: '#ffffff', color: '#111113', border: '#d4d4d8' },
};

export const DEFAULT_BUTTON_LABEL = 'Buy now';
export const BUTTON_LABEL_MAX_LENGTH = 40;

export function isButtonTheme(value: unknown): value is ButtonTheme {
  return value === 'brand' || value === 'dark' || value === 'light';
}

/**
 * The button's text: trimmed, whitespace collapsed, control characters
 * removed, capped, and never empty. Escaping happens separately, at the point
 * it is put into HTML.
 */
export function normaliseButtonLabel(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_BUTTON_LABEL;
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return Array.from(cleaned).slice(0, BUTTON_LABEL_MAX_LENGTH).join('').trim() || DEFAULT_BUTTON_LABEL;
}

/** Escape text for an HTML text node or a double-quoted attribute value. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The inline style every snippet carries, so the button looks right with no
 * script at all (a host that strips scripts, or a CSP that blocks ours). It
 * contains no quotes, so it sits inside a double-quoted attribute unescaped;
 * `Segoe UI` is a valid unquoted family name.
 */
export function buttonInlineStyle(theme: ButtonTheme): string {
  const t = BUTTON_THEMES[theme];
  return [
    'display:inline-block',
    'box-sizing:border-box',
    'padding:12px 24px',
    'border-radius:9999px',
    `border:1px solid ${t.border}`,
    `background:${t.background}`,
    `color:${t.color}`,
    'font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif',
    'font-size:16px',
    'font-weight:600',
    'font-style:normal',
    'line-height:1.2',
    // Reset the text properties a site's own link styles commonly set
    // (uppercase nav links, tracked-out letters, text shadows), which an inline
    // style beats as long as the site's rule is not !important.
    'letter-spacing:normal',
    'text-transform:none',
    'text-shadow:none',
    'text-indent:0',
    'text-align:center',
    'text-decoration:none',
    'cursor:pointer',
  ].join(';');
}

export type ButtonSnippetOptions = {
  /** The full payment-link URL, normally built with `source: 'button'`. */
  url: string;
  label?: string;
  theme?: ButtonTheme;
  /** Open checkout in a new tab so the seller's page stays open. Default true. */
  newTab?: boolean;
};

function anchorHtml({ url, label, theme, newTab }: ButtonSnippetOptions, extraAttributes: string): string {
  const resolvedTheme = isButtonTheme(theme) ? theme : 'brand';
  const text = normaliseButtonLabel(label);
  const target = newTab === false ? '' : ' target="_blank" rel="noopener"';
  return `<a href="${escapeHtml(url)}"${extraAttributes}${target} style="${buttonInlineStyle(resolvedTheme)}">${escapeHtml(text)}</a>`;
}

/** A plain link styled as a button. Works anywhere HTML is allowed. */
export function buyButtonHtml(options: ButtonSnippetOptions): string {
  return anchorHtml(options, '');
}

/**
 * The same link, marked up for `embed.js`, which adds one stylesheet for it
 * (hover and focus states, and resets that stop the host page's CSS restyling
 * it). The script never moves, hides or replaces the link, so the page keeps
 * full control of where and whether it shows, and the link still works, and
 * still looks right, if the script never loads.
 */
export function buyButtonEmbedHtml(options: ButtonSnippetOptions & { scriptOrigin: string }): string {
  const theme = isButtonTheme(options.theme) ? options.theme : 'brand';
  const scriptSrc = `${options.scriptOrigin.replace(/\/+$/, '')}/embed.js`;
  return [
    anchorHtml({ ...options, theme }, ` data-supa-buy data-theme="${theme}"`),
    `<script src="${escapeHtml(scriptSrc)}" async></script>`,
  ].join('\n');
}
